<template>
  <div>
    <h4 class="font-weight-bold">Columns</h4>
    <b-row class="mb-2">
      <b-col md="3">
        <div class="bv-search-with-clear">
          <b-form-input
              v-model="searchQuery"
              size="sm"
              placeholder="Search column"
          ></b-form-input>
          <p class="mb-0 small">Use this feature to search and select columns.</p>
          <b-icon-x-circle-fill
              sm
              v-if="searchQuery"
              class="clear-btn"
              @click.prevent="resetColumns"
          ></b-icon-x-circle-fill>
        </div>
      </b-col>
      <b-col md="4">
        <div class="ml-auto small d-flex align-items-center">
          <!-- Full Data Export (all users): the Lloyd's 5.2 columns are always
               included and shown locked/greyed, so the "Lloyds 5.2" and "All"
               filters (which select those columns) are hidden — users only
               manage Custom columns on top. -->
          <base-button
              v-if="!isFullDataExport"
              @click.prevent="selectFilter('lloyds')"
              :outline="'lloyds' != exportSubType"
              type="primary"
              size="sm"
          >Lloyds 5.2</base-button
          >
          <base-button
              v-if="!isFullDataExport"
              @click.prevent="selectFilter('default')"
              :outline="'default' != exportSubType"
              type="primary"
              size="sm"
          >All</base-button
          >
          <!-- In a Full Data Export this selects ALL custom fields on top of the
               locked 5.2 columns, so it reads "Select All"; in a normal export
               it's the "Custom" filter. -->
          <base-button
              @click.prevent="selectFilter('custom')"
              :outline="'custom' != exportSubType"
              type="primary"
              size="sm"
          >{{ isFullDataExport ? 'Select All' : 'Custom' }}</base-button
          >
          <base-button
              @click.prevent="clearSelection"
              type="danger"
              size="sm"
              class="ml-2"
          >Clear</base-button>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="12">
        <b-form-checkbox-group
            v-if="columnsOptions.length"
            v-model="modelValue"
            class="checkbox-group"
            stacked
            @change="onCheckboxChange"
        >
          <template v-for="item in columnsOptions">
            <!-- FDE only: the Lloyd's 5.2 header is a collapse toggle. Elsewhere
                 (and for the Custom header) it's a plain label. -->
            <div
                v-if="item.isHeader && isFullDataExport && item.group === 'lloyds52'"
                :key="item.key"
                class="group-header group-header--toggle"
                @click="lloyds52Collapsed = !lloyds52Collapsed"
            >
              <b-icon :icon="isLloyds52Collapsed ? 'chevron-right' : 'chevron-down'" class="mr-1" font-scale="0.8" />
              {{ item.text }}
            </div>
            <div v-else-if="item.isHeader" :key="item.key" class="group-header">{{ item.text }}</div>

            <b-form-checkbox
                v-else-if="!(isFullDataExport && item.group === 'lloyds52' && isLloyds52Collapsed)"
                :key="item.value"
                :value="item.value"
                :disabled="item.disabled"
                class="checkbox-item"
            >
              {{ item.text }}
            </b-form-checkbox>
          </template>
        </b-form-checkbox-group>
        <p v-else class="m-0 small">No items found.</p>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import Fuse from 'fuse.js'

import { default as columns } from './utils/exportColumnsNew.js'

const SUPERUSER_ONLY_CUSTOM_COLUMN_VALUES = new Set(['broker', 'fcp'])

export default {
  props: {
    value: {
      type: Array,
      default: () => []
    },
    exportType: {
      type: String
    },
    isTemplateLoaded: {
      type: Boolean,
      default: () => false
    },
    exportSubType: {
      type: String,
      default: () => "lloyds"
    },
    isSuperuser: {
      type: Boolean,
      default: false
    },
    isFullDataExport: {
      type: Boolean,
      default: false
    },
    isUnderwriter: {
      type: Boolean,
      default: false
    },
    jurisdictionList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchQuery: null,
      filterDefaults: false,
      checkboxChanged: false,
      // FDE only: the locked Lloyd's 5.2 group renders collapsed by default so
      // the editable Custom fields sit up top. Toggled via its header.
      lloyds52Collapsed: true,
    }
  },
  computed: {
    // The 5.2 group is treated as collapsed only when the toggle is set AND
    // there's no active search — a search must reveal matches inside the group.
    isLloyds52Collapsed() {
      return this.lloyds52Collapsed && !this.searchQuery
    },
    modelValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    columnsOptions() {
      // Exclude hidden and disabled columns from the checkbox list
      let results = columns.filter((column) => !column.hidden && !column.disabled)

      // TPA Group Name is available to underwriter exports only (for now).
      if (!this.isUnderwriter) {
        results = results.filter((column) => column.value !== 'TPA_Group_Name')
      }

      // Broker and FCP are superuser-only.
      if (!this.isSuperuser) {
        results = results.filter(
          (column) => !SUPERUSER_ONLY_CUSTOM_COLUMN_VALUES.has(String(column.value).trim().toLowerCase())
        )
      }

      if (this.isFullDataExport) {
        // AC5: jurisdiction fields excluded from Full Data Export for all users
        results = results.filter((column) => !column.region || column.region.length === 0)
        // The "Lloyd's 5.2" (non-custom) columns are ALWAYS included in a Full
        // Data Export and must not be changeable — for ALL users. Rather than
        // hide them, SHOW them checked and greyed out (disabled) so the user
        // can see they are selected and locked. They stay in the selected set
        // via ensureLloyds52Included. Custom columns remain selectable on top.
        results = results.map((column) =>
          column.custom ? column : { ...column, disabled: true }
        )
      } else {
        // Jurisdiction-specific columns only show once their region overlaps
        // a jurisdiction selected in the filter step.
        const jurisdictionList = this.jurisdictionList || []
        results = results.filter((column) =>
          !column.region || column.region.some((region) => jurisdictionList.includes(region))
        )
      }

      const fuse = new Fuse(results, {
        keys: ['text'],
        useExtendedSearch: true,
        threshold: 0.4
      })

      if (this.filterDefaults)
        results = results.filter((item) => !!item.default)

      if (this.searchQuery)
        results = fuse.search(`'${this.searchQuery}`).map(({ item }) => item)

      const baseCols = results.filter(c => !c.custom && (!c.region || c.region.length === 0))
      const jurisdictionCols = results.filter(c => !c.custom && c.region && c.region.length > 0)
      const customCols = results.filter(c => c.custom)

      const grouped = []

      if (baseCols.length) {
        // Tag the 5.2 header + its columns with a group key so the template can
        // collapse them behind an accordion header (FDE only, lighter touch).
        grouped.push({ key: 'header-regular', isHeader: true, text: 'Lloyds 5.2 Common Fields', group: 'lloyds52' })
        grouped.push(...baseCols.map((c) => ({ ...c, group: 'lloyds52' })))
      }

      // Full Data Export strips region columns out above (AC5), so this group
      // is only ever populated for normal exports.
      if (jurisdictionCols.length) {
        grouped.push({ key: 'header-jurisdiction', isHeader: true, text: 'Jurisdiction', group: 'jurisdiction' })
        grouped.push(...jurisdictionCols.map((c) => ({ ...c, group: 'jurisdiction' })))
      }

      if (customCols.length) {
        grouped.push({ key: 'header-custom', isHeader: true, text: 'Custom', group: 'custom' })
        grouped.push(...customCols.map((c) => ({ ...c, group: 'custom' })))
      }

      return grouped
    }
  },
  methods: {
    selectFilter(filter = undefined, justDisabled = false) {
      let selectedColumns = [...this.modelValue]
      let preSelectedColumns

      if (this.isTemplateLoaded) {
        preSelectedColumns = columns.filter((column) => selectedColumns.includes(column.value))
      } else {
        preSelectedColumns = columns
      }

      const isAllowed = (col) => {
        if (col.region && col.region.length > 0) {
          // AC5: jurisdiction fields excluded from Full Data Export for all users.
          if (this.isFullDataExport) return false
          // Otherwise only allow regions that overlap the selected jurisdictions.
          const jurisdictionList = this.jurisdictionList || []
          if (!col.region.some((region) => jurisdictionList.includes(region))) return false
        }
        // TPA Group Name is underwriter-only (for now).
        if (!this.isUnderwriter && col.value === 'TPA_Group_Name') return false
        // Broker and FCP are superuser-only.
        if (!this.isSuperuser && SUPERUSER_ONLY_CUSTOM_COLUMN_VALUES.has(String(col.value).trim().toLowerCase())) return false
        return true
      }

      // Full Data Export: the Lloyd's 5.2 columns are locked/mandatory and are
      // always kept. The "Custom" button selects ALL custom fields (its normal
      // job) on top of the mandatory set; any other filter just keeps the
      // current selection with the mandatory set re-pinned.
      if (this.isFullDataExport) {
        let fdeSelection = selectedColumns
        if ('custom' === filter && !justDisabled) {
          const allCustom = columns
            .filter((c) => c.custom && !c.hidden && isAllowed(c))
            .map((c) => c.value)
          fdeSelection = [...selectedColumns, ...allCustom]
        }
        // Superusers get TPA Group Name checked by default (they can still uncheck it).
        if (!this.isTemplateLoaded && !justDisabled && this.isSuperuser && this.isUnderwriter
            && !fdeSelection.includes('TPA_Group_Name')) {
          fdeSelection = [...fdeSelection, 'TPA_Group_Name']
        }
        this.$emit('export-sub-type', filter)
        this.$emit('input', this.withMandatoryColumns(fdeSelection))
        return
      }

      if ('default' === filter) {
        selectedColumns = preSelectedColumns.filter(isAllowed).map((item) => item.value)
        preSelectedColumns.forEach((col) => { col.default = true })
      } else if ('lloyds' === filter) {
        selectedColumns = preSelectedColumns
            .filter((item) => isAllowed(item) && !item.custom)
            .map((item) => item.value)
      } else if ('custom' === filter) {
        if (justDisabled) {
          selectedColumns = preSelectedColumns
              .filter((item) => item.disabled)
              .map((item) => item.value)
        } else {
          selectedColumns = preSelectedColumns.filter(isAllowed).map((item) => item.value)
        }
        preSelectedColumns.forEach((col) => { col.default = true })
      }

      this.$emit('export-sub-type', filter)
      this.$emit('input', selectedColumns)
    },
    clearSelection() {
      // Full Data Export: Clear removes the user's custom selections but keeps
      // the locked Lloyd's 5.2 columns (they are always included).
      if (this.isFullDataExport) {
        this.$emit('input', this.mandatoryColumns())
        return
      }
      this.selectFilter('custom', true)
    },
    resetColumns() {
      this.searchQuery = null
    },
    onCheckboxChange() {
      // Detect if columns were deselected in `lloyds` or `default` mode
      if ((this.exportSubType === 'lloyds' || this.exportSubType === 'default') && !this.checkboxChanged) {
        this.$emit('export-sub-type', 'custom')
      }
      this.checkboxChanged = false; // Reset flag after handling checkbox change
    },
    // The Lloyd's 5.2 (non-custom, non-region) columns that are ALWAYS included
    // in a Full Data Export — locked/greyed in the UI. Single source of truth.
    mandatoryColumns() {
      return columns
        .filter((c) => !c.custom && !c.hidden && (!c.region || c.region.length === 0))
        .map((c) => c.value)
    },
    // Merge the mandatory Lloyd's 5.2 columns into a selection, preserving the
    // user's custom picks. Used by the FDE filter buttons so no action can drop
    // a locked column.
    withMandatoryColumns(selected) {
      const current = Array.isArray(selected) ? selected : []
      return Array.from(new Set([...current, ...this.mandatoryColumns()]))
    },
    ensureLloyds52Included() {
      // Full Data Export (all users): the Lloyd's 5.2 (non-custom) columns are
      // shown locked/greyed and must ALWAYS be included in the export. Force
      // them into the selected set so they render checked and never drop out of
      // columns_to_show.
      if (!this.isFullDataExport) return
      const current = Array.isArray(this.modelValue) ? this.modelValue : []
      const merged = this.withMandatoryColumns(current)
      if (merged.length !== current.length) {
        this.$emit('input', merged)
      }
    }
  },
  mounted() {
    this.ensureLloyds52Included()
  },
  watch: {
    exportType: {
      immediate: true,
      handler(newVal) {
        this.selectFilter(this.exportSubType)
      }
    },
    isFullDataExport: {
      handler() {
        this.ensureLloyds52Included()
      }
    }
  }
}
</script>

<style lang="scss">
.bv-search-with-clear {
  position: relative;
}

.bv-search-with-clear .clear-btn {
  position: absolute;
  top: 32%;
  right: 8px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 0;
  font-size: 18px;
  line-height: 1;
  color: #6c757d; /* Bootstrap gray */
  cursor: pointer;
}

.bv-search-with-clear .clear-btn:hover {
  color: #000;
}
.checkbox-group {
  width: 100%;
  max-height: 325px;
  overflow-y: auto;

  .group-header {
    font-size: 16px;
    font-weight: 600;
    margin-top: 10px;
    margin-bottom: 8px;
    text-transform: uppercase;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 4px;
    color: #495057;

    &--toggle {
      cursor: pointer;
      user-select: none;
      display: flex;
      align-items: center;
    }
  }

  .checkbox-item {
    width: 25%;        // 4 per row
    display: inline-flex;
  }

  .form-check {
    display: flex;
  }

  .custom-control {
    width: 25%;

    &-label {
        font-size: 14px!important;
        line-height: 1.2!important;
        display: block!important;
    }
  }
}

</style>
